import React, { useState } from 'react';
import {
    TextField, 
    Paper, 
    Box, 
    Button, 
    Typography, 
    IconButton, 
    Checkbox, 
    FormControlLabel,
    Divider,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useIsMobile';
import { CloudUpload } from '@mui/icons-material';
import API from '../../api/axiosConfig';
import { useToast } from '../../context/ToastContext';
 

// Main application component for Event and Guest List Management
function MeetingForm() {

    const showToast = useToast();
    
    const navigate = useNavigate();

    // Checks if application is running on mobile
    const isMobile = useIsMobile();

    // Function used to get the current date and format it.
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // State variables for event details
    const [location, setLocation] = useState('Lib\'s Grill');
    const [date, setDate] = useState(getTodayDate());
    const [time, setTime] = useState('');
    const [description, setDescription] = useState('');
    const [meetingAttended, setMeetingAttended] = useState(true);
    const [amountSpent, setAmountSpent] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);

    // State variable for the guest list
    const [guests, setGuests] = useState([
        { id: 1, firstName: '', lastName: '', email: '', firstNameError: false, lastNameError: false, emailError: false }
    ]);

    // State for displaying messages (success or error)
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState(''); // 'success' or 'error'

    // State variable for loading
    const [loading, setLoading] = useState(false);


    // Function to add a new guest row to the list
    const addGuest = () => {
        const newGuestId = guests.length > 0 ? Math.max(...guests.map(g => g.id)) + 1 : 1;
        setGuests((prevGuests) => [
            ...prevGuests,
            { id: newGuestId, firstName: '', lastName: '', email: '', firstNameError: false, lastNameError: false, emailError: false }
        ]);
    };

    // Function to remove a guest row from the list
    const removeGuest = (idToRemove) => {
        setGuests((prevGuests) => prevGuests.filter((guest) => guest.id !== idToRemove));
    };

    // Function to handle input changes for guest fields (text fields)
    const handleGuestInputChange = (id, fieldName, value) => {
        setGuests((prevGuests) =>
            prevGuests.map((guest) =>
                guest.id === id
                    ? { ...guest, [fieldName]: value, [`${fieldName}Error`]: false } // Clear error on change
                    : guest
            )
        );
    };

    // Function to handle the single "Were you there?" checkbox change
    const handleMeetingAttendedChange = (event) => {
        setMeetingAttended(event.target.checked);
    };

    // Handle File Selection
  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

    // Function to validate all form fields (event details and guest list)
    const validateForm = () => {
        let isValid = true;
        let errorMessage = "";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email validation regex

        // Validate Event Details
        if (!location.trim()) {
            isValid = false;
            errorMessage = 'Location is required.';
        } else if (!date) {
            isValid = false;
            errorMessage = 'Date is required.';
        } else if (!time) {
            isValid = false;
            errorMessage = 'Time of Day is required.';
        } else if (!description.trim()) {
            isValid = false;
            errorMessage = 'Description is required.';
        }
        // Basic validation for amountSpent: ensure it's a valid number if provided
        if (amountSpent && isNaN(parseFloat(amountSpent))) {
            isValid = false;
            errorMessage = 'Amount Spent must be a valid number.';
        }

        // if amount spent is provided, receipt image must be provided
        const parsedAmount = parseFloat(amountSpent) || 0;
        if((parsedAmount>0 && !selectedFile) ||(selectedFile && parsedAmount === 0)){
            isValid = false;
            errorMessage = "Amount AND receipt image must be provided for reimbursement";
        }


        // Validate Guest List
        const updatedGuests = guests.map((guest) => {
                let guestIsValid = true;
                const updatedGuest = { ...guest };

                if (!guest.firstName.trim()) {
                    updatedGuest.firstNameError = true;
                    guestIsValid = false;
                } else {
                    updatedGuest.firstNameError = false;
                }

                if (!guest.lastName.trim()) {
                    updatedGuest.lastNameError = true;
                    guestIsValid = false;
                } else {
                    updatedGuest.lastNameError = false;
                }

                if (guest.email.trim() && !emailRegex.test(guest.email)) {
                    updatedGuest.emailError = true;
                    guestIsValid = false;
                    // only set error if another error hasn't already occurred
                    if (!errorMessage) {
                        errorMessage = 'Please ensure all required fields are filled and provided emails are valid.';
                    }
                } else {
                    // Email is either empty (which is fine) or valid
                    updatedGuest.emailError = false;
                }

                if (!guestIsValid) {
                    isValid = false; // If any guest is invalid, the whole form is invalid
                    if (messageType !== 'error') { // Only set message if not already set by event details
                        setMessage('Please ensure all guest fields are filled correctly, including valid emails.');
                        setMessageType('error');
                    }
                }
                return updatedGuest;
            });
        
        setGuests(updatedGuests);

        // If still no specific error message but form is invalid, set a generic one
        if (!isValid && !errorMessage) {
            errorMessage = 'Please fill out all required fields correctly.';
        }

        if(!isValid){
            setMessage(errorMessage);
            setMessageType("error");
        } else{
            // clear state
            setMessage("");
            setMessageType("");
        }

        return isValid;
    };

    const handleSubmit = async (event) => {
        
        event.preventDefault(); // Prevent default form submission behavior
        setLoading(true); // Start loading

        try {
            if (!validateForm()) {
                // Stop if validation fails
                // No need to manually set loading to false here
                // The 'return' will trigger the 'finally' block automatically!
                return;
            }
            const guestsToSubmit = guests.map(guest => ({
                firstName: guest.firstName,
                lastName: guest.lastName,
                email: guest.email.trim() === '' ? 'NONE' : guest.email.trim()
            }));

            const formData = new FormData();

            // JSON Object
            const meetingData = {
                location: location,
                date: date,
                time: time,
                description: description,
                meetingAttended: meetingAttended,
                amountSpent: amountSpent,
                guests: guestsToSubmit
            };

            // Append the JSON data as a Blob with application/json type.
            // This allows Spring Boot's @RequestPart("meeting") to parse it automatically.
            formData.append('meeting', new Blob([JSON.stringify(meetingData)], {
                type: 'application/json'
            }));

            // Append the file if it exists
            if (selectedFile) {
                formData.append('receipt', selectedFile);
            }

            //Value isnt used in Try block, so doesnt need to be saved to a variable
            await API.post('/api/meetings/add', formData);
            navigate("/meetings");
            return;  
        } catch(err) {
            const status = err.response?.status; 
            showToast("Error adding meeting!", "error")
            if (status === 400) {
                setMessage('ERROR: This meeting already exists or invalid data!');
                setMessageType('error');
                return;
            } else{
                setMessage("Error logging meeting! Please try again!");
                console.error("Error logging meeting: ", err);
            }
        } finally {
            // This runs no matter what (Success or Error)
            setLoading(false);
        }
    };

    return (
        <Box sx={{justifyContent: 'center', alignItems: 'center'}}>
            <Paper variant='main'>
                <form onSubmit={handleSubmit}>
                    {/* Meeting Details Section */}
                    <Box sx={{
                        padding: '16px',
                    }}>
                        <Typography variant="h6" sx={{ marginBottom: '16px', display: 'block' }}>
                            Meeting Details
                        </Typography>
                        <TextField
                            fullWidth
                            label="Location"
                            variant="outlined"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            sx={{ marginBottom: '16px' }}
                            required
                        />
                        {/* Grouping Date, Time, and Amount Spent on one line */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' }, // Stack on small screens, row on larger
                            gap: '16px', // Space between fields
                            marginBottom: '16px' // Margin after this row of fields
                        }}>
                            <TextField
                                fullWidth
                                label="Date"
                                type="date"
                                variant="outlined"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                slotProps={{inputLabel: {shrink: true}, input: { sx: { height: '56px', padding: '12px 12px' }}}}
                                sx={{ flex: 1, height: '56px' }} // Allow field to grow and shrink
                                required
                            />
                            <TextField
                                fullWidth
                                label="Time of Day"
                                type="time"
                                variant="outlined"
                                slotProps={{inputLabel: {shrink: true}, input: { sx: { height: '56px', padding: '12px 12px' }}}}
                                value={time}
                                onChange={(e) => setTime(e.target.value)}
                                sx={{ flex: 1, height: '56px' }} // Allow field to grow and shrink
                                required
                            />
                            <TextField
                                fullWidth
                                label="Amount Spent (USD)"
                                variant="outlined"
                                type="number"
                                value={amountSpent}
                                onChange={(e) => setAmountSpent(e.target.value)}
                                sx={{ flex: 1 }} // Allow field to grow and shrink
                            />
                        </Box>

                        <TextField
                            fullWidth
                            label="Description"
                            variant="outlined"
                            multiline
                            rows={4}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            sx={{ marginBottom: '16px' }}
                        />
                        {/* Single Checkbox for "Were you there?" */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={meetingAttended}
                                    onChange={handleMeetingAttendedChange}
                                    name="meetingAttended"
                                    sx={(theme) => ({
                                        color: theme.palette.paper.light,
                                        '&.Mui-checked': {
                                            color: theme.palette.paper.verylight,
                                        },
                                    })}
                                />
                            }
                            label="Were you there?"
                            sx={(theme) => ({
                                marginTop: '8px',
                                marginBottom: '8px',
                            })}
                        />
                    </Box>

                    <Divider sx={(theme) => ({ 
                        borderColor: theme.palette.paper.light,
                        borderBottomWidth: '1px', // This ensures it is a visible line
                        mb: 1
                    })}/>

                    {/* Guest List Section */}
                    <Box sx={{
                        padding: '16px',
                        borderRadius: '8px'
                    }}>
                        <Typography variant="h6" sx={{ marginBottom: '16px', display: 'block' }}>
                            Other Attendees
                        </Typography>
                        {guests.map((guest, index) => (
                            <Box
                                key={guest.id}
                                sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: 'center',
                                    gap: '16px',
                                    padding: '12px',
                                    marginBottom: index < guests.length - 1 ? '16px' : '0'
                                }}
                            >
                                <TextField
                                    label="First Name"
                                    variant="outlined"
                                    value={guest.firstName}
                                    onChange={(e) => handleGuestInputChange(guest.id, 'firstName', e.target.value)}
                                    error={guest.firstNameError}
                                    helperText={guest.firstNameError ? 'First name is required' : ''}
                                    sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                                    required
                                />
                                <TextField
                                    label="Last Name"
                                    variant="outlined"
                                    value={guest.lastName}
                                    onChange={(e) => handleGuestInputChange(guest.id, 'lastName', e.target.value)}
                                    error={guest.lastNameError}
                                    helperText={guest.lastNameError ? 'Last name is required' : ''}
                                    sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                                    required
                                />
                                <TextField
                                    label="Email"
                                    variant="outlined"
                                    type="email"
                                    value={guest.email}
                                    onChange={(e) => handleGuestInputChange(guest.id, 'email', e.target.value)}
                                    error={guest.emailError}
                                    helperText={guest.emailError ? 'Valid email is required' : ''}
                                    sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
                                />
                                {guests.length > 1 && (
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => removeGuest(guest.id)}
                                        sx={(theme) => ({ marginTop: { xs: '8px', sm: '0' }, color: theme.palette.paper.verylight })}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={addGuest}
                            sx={{
                                width: isMobile ? '256px' : '512px',
                                mt: 2
                            }}
                        >
                            Add Guest
                        </Button>
                    </Box>

                    {/* Message Display */}
                    {message && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: messageType === 'success' ? '#d4edda' : '#f8d7da',
                                color: messageType === 'success' ? '#155724' : '#721c24',
                                borderColor: messageType === 'success' ? '#c3e6cb' : '#f5c6cb',
                                border: '1px solid',
                                marginBottom: '32px'
                            }}
                        >
                            <Typography>{message}</Typography>
                        </Box>
                    )}

                    <Divider sx={(theme) => ({ 
                        borderColor: theme.palette.paper.light,
                        borderBottomWidth: '1px', // This ensures it is a visible line
                        mb: 4,
                        mt: 2
                    })}/>

                    {/* Reimbursement File Attachment */}
                    <Typography variant="h6" sx={{ marginBottom: '16px', display: 'block' }}>
                        Reimbursement
                    </Typography>
                    <Box sx={{ border: '1px dashed grey', p: 2, textAlign: 'center', borderRadius: 1 }}>
                        <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="receipt-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="receipt-file-upload">
                            <Button variant="outlined" component="span" startIcon={<CloudUpload/>}>
                                Upload Receipt Image
                            </Button>
                        </label>
                        {selectedFile && (
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                                Selected: {selectedFile.name}
                            </Typography>
                        )}
                    </Box>

                    <Divider sx={(theme) => ({ 
                        borderColor: theme.palette.paper.light,
                        borderBottomWidth: '1px', // This ensures it is a visible line
                        mb: 4,
                        mt: 2
                    })}/>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            width: isMobile ? '256px' : '512px',
                        }}
                        startIcon={
                             loading ? <CircularProgress size={20} /> : null
                        }
                    >
                        {loading ? "Processing..." : "Save Meeting Information"}
                    </Button>
                    
                </form>
            </Paper>
        </Box>
    );
}

export default MeetingForm;